import { useCallback, useEffect, useRef, type RefObject } from 'react';

import type { VirtualizerHandle } from 'virtua';

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Persists virtual-list offsets across navigation, including a switch before
 * the debounced write completes. Mount the hook with the list's identity.
 */
export function useVListScrollPersistence(
  vlistRef: RefObject<VirtualizerHandle | null>,
  storageKey: string,
  options: { debounceMs?: number; ready?: boolean } = {}
): (offset: number) => void {
  const { debounceMs = DEFAULT_DEBOUNCE_MS, ready = true } = options;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingOffsetRef = useRef<number | undefined>(undefined);
  const restoredKeyRef = useRef<string | undefined>(undefined);

  const saveOffset = useCallback(() => {
    if (pendingOffsetRef.current === undefined) return;
    try {
      sessionStorage.setItem(storageKey, String(pendingOffsetRef.current));
    } catch {
      // The browser can deny session storage
    }
    pendingOffsetRef.current = undefined;
  }, [storageKey]);

  useEffect(() => {
    if (!ready || restoredKeyRef.current === storageKey) return;

    const frame = requestAnimationFrame(() => {
      if (!vlistRef.current) return;
      restoredKeyRef.current = storageKey;
      try {
        const offset = Number(sessionStorage.getItem(storageKey) ?? 0);
        if (Number.isFinite(offset) && offset > 0)
          vlistRef.current.scrollTo(offset);
      } catch {
        // The list remains usable without restoration
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [ready, storageKey, vlistRef]);

  useEffect(
    () => () => {
      clearTimeout(saveTimerRef.current);
      saveOffset();
    },
    [saveOffset]
  );

  return useCallback(
    (offset: number) => {
      if (restoredKeyRef.current !== storageKey) return;
      pendingOffsetRef.current = offset;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(saveOffset, debounceMs);
    },
    [storageKey, saveOffset, debounceMs]
  );
}
